// Configure webpack publicPath for chunk loading (standalone + embedded in resto-app-admin).
// MUST be imported first in index.js.
//
// When embedded, `public/admin-stock-loader.js` sets `window.__ADM_STOCK_PUBLIC_PATH__` *before*
// appending `main.js`. Without that, scanning <script> tags can pick the host app’s last injected
// script (e.g. :3050 bundle) and send chunk requests to the wrong origin.

if (process.env.REACT_APP_MFE_SHARED === 'true') {
  const shared = typeof window !== 'undefined' ? window.__MFE_SHARED__ : null;
  if (!shared || !shared.react || !shared['react-dom/client'] || !shared['@mui/material']) {
    throw new Error(
      '[mfe-shared] window.__MFE_SHARED__ is missing React/MUI. Embed inside resto-app-admin, or rebuild without REACT_APP_MFE_SHARED.',
    );
  }
}

// --- Chunk load recovery ---------------------------------------------------

const CHUNK_RELOAD_KEY = 'adm_stock_chunk_reload_ts';
const RELOAD_COOLDOWN_MS = 5000;

function isChunkError(error) {
  if (!error) return false;
  const err = error;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message != null ? err.message : error);
  return /Loading chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module|\(missing:/i.test(
    msg,
  );
}

function canReload() {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const ts = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!ts) return true;
    const last = parseInt(ts, 10);
    return Number.isNaN(last) || Date.now() - last > RELOAD_COOLDOWN_MS;
  } catch (e) {
    return true;
  }
}

function doChunkReload() {
  if (!canReload()) {
    console.error(
      '[adm-stock] Chunk load error persists. Hard refresh (Ctrl+Shift+R) or rebuild & restart the app on port 3001.',
    );
    return;
  }
  console.warn('[adm-stock] Chunk load error — reloading to fetch a fresh main + chunks…');
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    /* ignore */
  }
  window.location.reload();
}

function scriptFailureShouldReload(scriptSrc) {
  if (!scriptSrc || typeof window === 'undefined') return false;
  try {
    const u = new URL(scriptSrc, window.location.href);
    if (!/\/static\/js\//.test(u.pathname)) return false;
    const injected = window.__ADM_STOCK_PUBLIC_PATH__;
    if (injected) {
      const base = new URL(injected, window.location.href);
      return u.origin === base.origin;
    }
    const resolved = window.__ADM_STOCK_CHUNK_ORIGIN__;
    if (resolved) {
      return u.origin === resolved;
    }
  } catch (e) {
    return false;
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'unhandledrejection',
    (e) => {
      if (isChunkError(e.reason)) {
        e.preventDefault();
        doChunkReload();
      }
    },
    false,
  );

  // Capture: failed dynamic import / chunk promises often surface here.
  window.addEventListener(
    'error',
    (e) => {
      const el = e.target;
      if (el && el.tagName === 'SCRIPT' && el.src && scriptFailureShouldReload(el.src)) {
        e.preventDefault();
        doChunkReload();
        return;
      }
      if (isChunkError(e.error) || isChunkError(e.message)) {
        e.preventDefault();
        doChunkReload();
      }
    },
    true,
  );

  window.addEventListener(
    'load',
    () => {
      setTimeout(() => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        } catch (e) {
          /* ignore */
        }
      }, 2000);
    },
    { once: true },
  );
}

export const chunkErrorUtils = {
  isChunkError,
  doChunkReload,
  CHUNK_RELOAD_KEY,
};

// --- publicPath ------------------------------------------------------------

let baseUrl = '';
let resolvedFromScript = false;

if (typeof window !== 'undefined') {
  const injected = window.__ADM_STOCK_PUBLIC_PATH__;
  if (injected && typeof injected === 'string') {
    baseUrl = injected.trim();
    resolvedFromScript = true;
  } else {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i -= 1) {
      const src = scripts[i].src;
      if (!src || !src.includes('/static/js/')) continue;
      // Prefer this app’s entry, not the host shell’s bundle.*.js on another origin/port.
      const isMainLike =
        /\/main(\.[a-f0-9]{8,})?\.js(\?|$)/i.test(src) ||
        (/\/bundle\.js(\?|$)/i.test(src) && new URL(src, window.location.href).origin === window.location.origin);
      if (!isMainLike) continue;
      try {
        const u = new URL(src, window.location.href);
        const staticJs = '/static/js/';
        const pos = u.pathname.indexOf(staticJs);
        if (pos !== -1) {
          baseUrl = `${u.origin}${u.pathname.slice(0, pos)}/`;
        } else {
          baseUrl = `${u.origin}${u.pathname.replace(/\/[^/]+\.js(\?.*)?$/, '/')}`;
        }
        resolvedFromScript = true;
        break;
      } catch (e) {
        /* ignore */
      }
    }
  }

  if (!baseUrl) {
    baseUrl = `${window.location.protocol}//${window.location.host}/`;
  }

  const host = window.location.hostname;
  const isLocalNetwork =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    /^192\.168\./.test(host) ||
    /^10\./.test(host) ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host);

  if (!resolvedFromScript && !isLocalNetwork) {
    const cdnBase =
      process.env.REACT_APP_CDN_BASE_URL || 'https://d3p4ulpztjstyj.cloudfront.net';
    baseUrl = cdnBase.endsWith('/') ? cdnBase : `${cdnBase}/`;
  }

  try {
    window.__ADM_STOCK_CHUNK_ORIGIN__ = new URL(baseUrl, window.location.href).origin;
  } catch (e) {
    /* ignore */
  }
}

// eslint-disable-next-line no-undef
__webpack_public_path__ = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

// Hook into webpack's chunk loading to catch failures at the source.
// This runs after webpack runtime is initialized but before lazy chunks load.
if (typeof window !== 'undefined') {
  // Intercept chunk load failures via the jsonp callback array's push method
  // This is a last-resort hook for when other handlers miss the error.
  const patchedArrays = new WeakSet();

  const patchJsonpArray = (arr) => {
    if (!arr || patchedArrays.has(arr)) return;
    patchedArrays.add(arr);
    const origArrPush = arr.push.bind(arr);
    arr.push = function (...args) {
      try {
        return origArrPush(...args);
      } catch (e) {
        if (isChunkError(e)) {
          doChunkReload();
        }
        throw e;
      }
    };
  };

  // Patch any existing jsonp arrays
  Object.keys(window).forEach((key) => {
    if (/^webpackJsonp/.test(key) && Array.isArray(window[key])) {
      patchJsonpArray(window[key]);
    }
  });
}
